import { matchPath } from "react-router-dom"
import { sl2Catalog } from "appsShared/inAppMessages"
import { iamActionsMap } from "shared/iamActionData"
import type { Locker } from "shared/locker"
import type { Tenant } from "shared/tenant"
import type { TenantUser } from "shared/tenantUser"
import type { User } from "shared/user"
import type { Sl2Mode } from "../../ModeProvider"

/**
 * The SL2 audience context, typed FROM the DEV-446 catalog: one key per catalog attribute,
 * each with the value type the catalog declares (enum → its values). A key the catalog does
 * not have, a missing key, or a wrong value type fails the wfe typecheck (LOCKER-483 DoD8).
 * `undefined` = no verified value on this page, which fails every operator except `notExists`.
 */
type CatalogAttr = (typeof sl2Catalog.attributes)[number]
type ValueOf<A> = A extends { type: "enum"; values: readonly (infer V)[] }
  ? V
  : A extends { type: "string" }
    ? string
    : A extends { type: "list" }
      ? string[]
      : A extends { type: "number" }
        ? number
        : never
export type Sl2MessageContext = {
  [A in CatalogAttr as A["key"]]: ValueOf<A> | undefined
}

/** The OS locker pages (`App.tsx`: detail and `/controller`), the only pages with a current locker in the store. */
export const LOCKER_PAGE_ROUTE = "/os/lockers/:lockerId/*"

export type Sl2MessageContextSources = {
  tenantId?: string
  tenant?: Tenant
  user?: User
  tu?: TenantUser
  /** `useFeatures(tenantId).activatedFeatures`; undefined when no tenant is selected. */
  activatedFeatures?: string[]
  /** Every locker thing in the store; undefined until the tenant's `GetLockers` query succeeded. */
  lockers?: Locker[]
  language: string
  mode: Sl2Mode
  pathname: string
  uiSurfaces?: string[]
  width: number
}

/**
 * `superAdmin` = the `/me` user's `isSuperAdmin` flag (the gate `App.tsx` uses for `/sa/*`).
 * `tenantAdmin` = the tenant user holds `mod:modIam:access`, the action that shows the
 * Users & Access module (`OsLayout` `osMenuItems`, AdminGroup). Otherwise a tenant user is
 * an `operator`. No tenant user and not a super admin → undefined.
 */
export function sl2UserRole(
  user?: User,
  tu?: TenantUser,
): Sl2MessageContext["user.role"] {
  if (user?.isSuperAdmin) return "superAdmin"
  if (!tu) return undefined
  return tu.iamActionRefNames?.includes(iamActionsMap.ModIamAccess)
    ? "tenantAdmin"
    : "operator"
}

/** Pure: every input comes from state the console already holds (spec §Flows › Consume). */
export function buildSl2MessageContext(
  s: Sl2MessageContextSources,
): Sl2MessageContext {
  const tenantLockers = s.tenantId
    ? s.lockers?.filter((l) => l.tenantId === s.tenantId)
    : undefined
  const lockerId = matchPath(LOCKER_PAGE_ROUTE, s.pathname)?.params.lockerId
  const locker = lockerId
    ? s.lockers?.find((l) => l.id === lockerId)
    : undefined
  return {
    "tenant.id": s.tenantId || undefined,
    "tenant.name": s.tenant?.name || undefined,
    // `sstApiVersion` absent means v1 ("default/legacy", `shared/locker.d.ts`).
    "locker.sstApiVersion": locker ? (locker.sstApiVersion ?? "v1") : undefined,
    "tenant.features": s.tenantId ? s.activatedFeatures : undefined,
    "tenant.lockerCount": tenantLockers?.length,
    "user.role": sl2UserRole(s.user, s.tu),
    "user.locale": s.language.startsWith("en") ? "en" : "fr",
    "session.mode": s.mode,
    "page.route": s.pathname,
    "page.uiSurfaces": s.uiSurfaces,
    "device.width": s.width,
  }
}
